import { useState, useEffect, useCallback } from 'react';
import * as Sentry from "@sentry/react";
import { getPublicProfile, getMyProfile, updateMyProfile, followUser, unfollowUser } from '../services/api';
import { AuthError } from "../errors/sentryErrors";

export const useProfile = (handle) => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    if (!handle) return;
    try {
      setIsLoading(true);
      setError(null);
      const res = await getPublicProfile(handle);
      setData(res);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "auth");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new AuthError(err instanceof Error ? err.message : String(err)));
      });
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [handle]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { data, isLoading, error, refetch: fetchProfile, setData };
};

export const useMyProfile = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchProfile = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await getMyProfile();
      setData(res);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "auth");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        Sentry.captureException(new AuthError(err instanceof Error ? err.message : String(err)));
      });
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const mutate = async (updateData) => {
    try {
      const res = await updateMyProfile(updateData);
      setData(res);
      return res;
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { data, isLoading, error, refetch: fetchProfile, mutate };
};

export const useFollow = () => {
  const follow = async (handle) => {
    return await followUser(handle);
  };

  const unfollow = async (handle) => {
    return await unfollowUser(handle);
  };

  return { follow, unfollow };
};
